import {Mapper} from '@s-ui/domain'

export default class SeoDataMapper extends Mapper {
  constructor({mapper, config} = {}) {
    super({mapper, config})

    this._title = ''
    this._metas = []
    this._links = []
    this._schemaData = []
    this._mapper = mapper
    this._config = config
    this._seoConfig = this._config.get('seo')
    this._useGraphFormat = false
  }

  _getSchema = ({schemaData} = {}) =>
    schemaData
      ? {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(schemaData)
        }
      : {}

  _isDevEnv = () => this._config.get('IS_DEV')

  /**
   * @param {String}
   * @returns {Object}
   */
  title(title) {
    this._title = title
    return this
  }

  /**
   * @param {String}
   * @returns {Object}
   */
  description(description) {
    this._metas.push({name: 'description', content: description})
    return this
  }

  /**
   * @param {String}
   * @returns {Object}
   */
  robots(robots) {
    const content = this._isDevEnv() ? this._seoConfig.DEVELOPMENT_META_ROBOTS : robots

    this._metas.push({name: 'robots', content})

    return this
  }

  /**
   * @param {String}
   * @returns {Object}
   */
  robotsExtended(robots) {
    this._metas.push({name: 'robots', content: robots})

    return this
  }

  /**
   * @param {String}
   * @returns {Object}
   */
  canonical(canonical) {
    canonical && this._links.push({rel: 'canonical', href: canonical})
    return this
  }

  /**
   * @param {Object}
   * @returns {Object}
   */
  alternate(alternate) {
    if (!alternate) return this

    const {TYPE: type, TITLE: title, URL: href} = alternate
    this._links.push({rel: 'alternate', type, title, href})
    return this
  }

  /**
   * @param {Object}
   * @returns {Object}
   */
  addSchemaData(schemaData) {
    if (schemaData) this._schemaData.push(this._getSchema({schemaData}))
    return this
  }

  /**
   * Enable @graph format for schema data
   * @returns {Object}
   */
  useGraphFormat() {
    this._useGraphFormat = true
    return this
  }

  setPaginationLinks({next, prev}) {
    next && this._links.push({rel: 'next', href: next})
    prev && this._links.push({rel: 'prev', href: prev})
    return this
  }

  _getScripts = () => {
    return this._schemaData
  }

  /**
   * Combines all schema data into a single @graph structure
   * @returns {Array} - Array with single script containing @graph
   */
  _getGraphScript = () => {
    if (this._schemaData.length === 0) return []

    const graphItems = this._schemaData.flatMap(script => {
      const schemaData = JSON.parse(script.innerHTML)

      // If schemaData is an array, process each item
      if (Array.isArray(schemaData)) {
        return schemaData.map(item => {
          const {'@context': context, ...rest} = item
          return rest
        })
      }

      // Remove @context from individual items as it will be at the root
      const {'@context': context, ...rest} = schemaData
      return rest
    })

    const graphSchema = {
      '@context': 'https://schema.org',
      '@graph': graphItems
    }

    return [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(graphSchema)
      }
    ]
  }

  /**
   * @param {Array}
   * @returns {Object}
   */
  setPreloadImages({preloadImages}) {
    preloadImages.map(href => this._links.push({rel: 'preload', as: 'image', href}))
    return this
  }

  /**
   * @param {Object}
   * @returns {Object}
   */
  setOpenGraphData({title, description, url, image}) {
    const {
      DEFAULT_OPEN_GRAPH_TITLE: defaultTitle,
      DEFAULT_OPEN_GRAPH_DESCRIPTION: defaultDescription,
      DEFAULT_OPEN_GRAPH_IMAGE
    } = this._seoConfig

    const urlStatics = this._config.get('urlStatics')
    const defaultImage = `${urlStatics}${DEFAULT_OPEN_GRAPH_IMAGE}`

    this._metas.push({property: 'og:type', content: 'website'})
    this._metas.push({property: 'og:title', content: title || defaultTitle})
    this._metas.push({
      property: 'og:description',
      content: description || defaultDescription
    })
    this._metas.push({property: 'og:image', content: image || defaultImage})

    if (url) this._metas.push({property: 'og:url', content: url})

    return this
  }

  /**
   * @param {Object}
   * @returns {Object}
   */
  setTwitterCardsData() {
    const twitterAccount = this._config.get('TWITTER_ACCOUNT_NAME')

    this._metas.push({name: 'twitter:card', content: 'summary'})
    this._metas.push({name: 'twitter:site', content: twitterAccount})

    return this
  }

  map = () => ({
    title: this._title,
    metas: this._metas,
    links: this._links,
    scripts: this._useGraphFormat ? this._getGraphScript() : this._getScripts()
  })
}
